import React, { useState, useEffect } from 'react';
import { Globe, BookOpen, CheckCircle, AlertCircle, Zap } from 'lucide-react';
import { WizardData } from '../AIActivityWizard';
import {
  CurriculumSystem,
  CurriculumLevel,
  CurriculumSelection,
  getCurriculumSystems,
  getCurriculumLevels,
  getCurriculumLevelForAge,
  detectCurriculumLevel,
  COUNTRY_NAMES
} from '../../lib/curriculum-systems';

interface CurriculumSelectionStepProps {
  wizardData: WizardData;
  onStepComplete: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  setIsGenerating: (generating: boolean) => void;
}

export function CurriculumSelectionStep({ wizardData, onStepComplete }: CurriculumSelectionStepProps) {
  const [curriculumSystems, setCurriculumSystems] = useState<CurriculumSystem[]>([]);
  const [curriculumLevels, setCurriculumLevels] = useState<CurriculumLevel[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<CurriculumSystem | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<CurriculumLevel | null>(null);
  const [autoDetectedLevel, setAutoDetectedLevel] = useState<CurriculumLevel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentChild = wizardData.createdChild || wizardData.selectedChild;
  const childAge = currentChild?.age || wizardData.childData?.age || 8;

  // Initialize from existing wizard data
  useEffect(() => {
    if (wizardData.curriculumSelection) {
      // Find and set the previously selected system and level
      loadExistingSelection();
    }
  }, [wizardData.curriculumSelection]);

  // Load curriculum systems on mount
  useEffect(() => {
    loadCurriculumSystems();
  }, []);

  // Load levels when system is selected
  useEffect(() => {
    if (selectedSystem) {
      loadCurriculumLevels();
      autoDetectLevel();
    }
  }, [selectedSystem, childAge]);

  const loadExistingSelection = async () => {
    if (!wizardData.curriculumSelection) return;

    try {
      const systems = await getCurriculumSystems();
      const system = systems.find(s => s.id === wizardData.curriculumSelection?.systemId);
      if (system) {
        setSelectedSystem(system);
        const levels = await getCurriculumLevels(system.id);
        setCurriculumLevels(levels);
        const level = levels.find(l => l.id === wizardData.curriculumSelection?.levelId);
        if (level) {
          setSelectedLevel(level);
        }
      }
    } catch (err) {
      console.error('Error loading existing selection:', err);
    }
  };

  const loadCurriculumSystems = async () => {
    try {
      setLoading(true);
      const systems = await getCurriculumSystems();
      setCurriculumSystems(systems);
      setError(null);
    } catch (err) {
      setError('Failed to load curriculum systems');
      console.error('Error loading curriculum systems:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurriculumLevels = async () => {
    if (!selectedSystem) return;

    try {
      const levels = await getCurriculumLevels(selectedSystem.id);
      setCurriculumLevels(levels);
    } catch (err) {
      console.error('Error loading curriculum levels:', err);
    }
  };

  const autoDetectLevel = async () => {
    if (!selectedSystem || !childAge) return;

    try {
      // Try database function first
      const detectedLevel = await getCurriculumLevelForAge(childAge, selectedSystem.id);
      if (detectedLevel) {
        setAutoDetectedLevel(detectedLevel);
        // Auto-select if no level is currently selected
        if (!selectedLevel) {
          setSelectedLevel(detectedLevel);
          updateCurriculumSelection(selectedSystem, detectedLevel, true);
        }
        return;
      }

      // Fallback to static detection
      const levelCode = detectCurriculumLevel(childAge, selectedSystem.country_code);
      if (levelCode) {
        const level = curriculumLevels.find(l => l.level_code === levelCode);
        if (level) {
          setAutoDetectedLevel(level);
          if (!selectedLevel) {
            setSelectedLevel(level);
            updateCurriculumSelection(selectedSystem, level, true);
          }
        }
      }
    } catch (err) {
      console.error('Error auto-detecting curriculum level:', err);
    }
  };

  const handleSystemSelect = (system: CurriculumSystem) => {
    setSelectedSystem(system);
    setSelectedLevel(null);
    setAutoDetectedLevel(null);
  };

  const handleLevelSelect = (level: CurriculumLevel) => {
    setSelectedLevel(level);
    if (selectedSystem) {
      updateCurriculumSelection(selectedSystem, level, false);
    }
  };

  const updateCurriculumSelection = (system: CurriculumSystem, level: CurriculumLevel, autoDetected: boolean) => {
    const selection: CurriculumSelection = {
      systemId: system.id,
      levelId: level.id,
      countryCode: system.country_code,
      autoDetected
    };

    onStepComplete({
      curriculumSelection: selection
    });
  };

  const getSystemFlag = (countryCode: string): string => {
    const flags: Record<string, string> = {
      GBR: '🇬🇧',
      AUS: '🇦🇺',
      USA: '🇺🇸',
      CAN: '🇨🇦',
      NZL: '🇳🇿'
    };
    return flags[countryCode] || '🌍';
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Globe className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-gray-600">Loading curriculum systems...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Curricula</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadCurriculumSystems}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Select Educational Curriculum
        </h3>
        <p className="text-gray-600">
          Choose the curriculum system to align activities with {currentChild?.name || 'your child'}'s education
        </p>
      </div>

      {/* Curriculum System Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Globe className="w-5 h-5 mr-2 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">Country & Curriculum System</h4>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {curriculumSystems.map((system) => (
            <div
              key={system.id}
              onClick={() => handleSystemSelect(system)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedSystem?.id === system.id
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-2xl">{getSystemFlag(system.country_code)}</div>
                {selectedSystem?.id === system.id && (
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <h5 className="font-semibold text-gray-900 mb-1">{COUNTRY_NAMES[system.country_code]}</h5>
              <p className="text-sm text-gray-600">{system.system_name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Curriculum Level Selection */}
      {selectedSystem && curriculumLevels.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
              <h4 className="text-lg font-semibold text-gray-900">Education Level</h4>
            </div>
            {autoDetectedLevel && (
              <div className="flex items-center text-sm text-green-600">
                <Zap className="w-4 h-4 mr-1" />
                <span>Auto-detected for age {childAge}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            {curriculumLevels.map((level) => {
              const isAutoDetected = autoDetectedLevel?.id === level.id;
              const isSelected = selectedLevel?.id === level.id;
              
              return (
                <div
                  key={level.id}
                  onClick={() => handleLevelSelect(level)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                      : isAutoDetected
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h5 className="font-semibold text-gray-900">{level.level_name}</h5>
                        {level.grade_equivalent && (
                          <span className="text-sm text-gray-500">({level.grade_equivalent})</span>
                        )}
                        {isAutoDetected && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            Suggested
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        Ages {level.age_range_start}-{level.age_range_end}
                      </p>
                      {level.description && (
                        <p className="text-xs text-gray-500">{level.description}</p>
                      )}
                    </div>
                    <div className="ml-4">
                      {isSelected ? (
                        <CheckCircle className="w-6 h-6 text-purple-600" />
                      ) : isAutoDetected ? (
                        <Zap className="w-6 h-6 text-green-600" />
                      ) : (
                        <div className="w-6 h-6 border-2 border-gray-300 rounded-full" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selection Summary */}
      {selectedSystem && selectedLevel && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
            Curriculum Selection Complete
          </h4>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Education System:</h5>
              <p className="text-gray-700">
                {getSystemFlag(selectedSystem.country_code)} {selectedSystem.system_name}
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Level:</h5>
              <p className="text-gray-700">
                {selectedLevel.level_name} (Ages {selectedLevel.age_range_start}-{selectedLevel.age_range_end})
              </p>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white bg-opacity-60 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              Activities will be aligned with <strong>{selectedSystem.system_name}</strong> standards 
              for <strong>{selectedLevel.level_name}</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}